import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { z } from "zod";

import type { PaymentDocument } from "@ravnar/api-contract";

import { ApiError } from "../../../api/client";
import { paymentsPageQueryOptions } from "../../../api/payments";
import { useSession } from "../../../api/session";
import { buttonClassName } from "../../../components/ui/button-styles";
import { Card, CardHeader } from "../../../components/ui/card";
import { Empty } from "../../../components/ui/empty";
import { FilterPill, Toolbar } from "../../../components/ui/filter-pill";
import { Pager } from "../../../components/ui/pager";
import { Pill } from "../../../components/ui/pill";
import { Skeleton } from "../../../components/ui/skeleton";
import { Table, type TableColumn } from "../../../components/ui/table";
import {
	PAYMENT_DOCUMENT_KIND_LABELS,
	PAYMENT_STATUS_LABELS,
	PAYMENT_STATUS_TONES,
	PAYMENT_STATUSES,
	type PaymentSide,
	type PaymentStatus,
} from "../../../domain/payments";
import { cn } from "../../../lib/cn";
import { formatAmount, formatShortDate } from "../../../lib/format";
import { PaymentDocumentDrawer } from "./payment-document-drawer";

// `.catch()` turns a hand-edited bad value into the fallback.
/** What the Payables and the Receivables pages keep in their address: the status, the page and the open document. */
export const paymentsSearchSchema = z.object({
	/** The status the list is narrowed to; every status when there is none. */
	status: z.enum(PAYMENT_STATUSES).optional().catch(undefined),
	/** A page link from the API, which the page never reads; the first page when there is none. */
	page: z.string().optional().catch(undefined),
	/** The document the drawer shows; one no page follows says so there. */
	document: z.string().optional().catch(undefined),
});
export type PaymentsSearch = z.infer<typeof paymentsSearchSchema>;

/** The path of each side's page. */
const SIDE_ROUTES = { payables: "/finance/payables", receivables: "/finance/receivables" } as const;

/** What the card says when the list it shows is empty: a status with none, or a year with none. */
const EMPTY_TEXT: Readonly<Record<PaymentSide, Readonly<Record<PaymentStatus | "all", string>>>> = {
	payables: {
		all: "No bill or reimbursement claim of this year yet.",
		overdue: "Nothing is overdue.",
		open: "Nothing is waiting to be paid.",
		paid: "Nothing is paid yet.",
		settled: "Nothing is settled by hand.",
	},
	receivables: {
		all: "No invoice of this year yet.",
		overdue: "No invoice is overdue.",
		open: "No invoice is waiting to be paid.",
		paid: "No invoice is paid yet.",
		settled: "No invoice is settled by hand.",
	},
};

function plural(count: number, singular: string, pluralForm: string): string {
	return `${String(count)} ${count === 1 ? singular : pluralForm}`;
}

interface PaymentDocumentsProps {
	readonly side: PaymentSide;
	readonly search: PaymentsSearch;
	/** Replaces what the address keeps. */
	readonly onSearch: (next: PaymentsSearch) => void;
}

/**
 * One side's documents of the year, a page at a time — the overdue first, then the open, then the paid and the
 * settled — narrowed to one status by the pills, which count every status of the side whichever is shown, and the
 * document the address names in a drawer. Only a member who may read Finance is sent any of it.
 */
export function PaymentDocuments({ side, search, onSearch }: PaymentDocumentsProps) {
	const { member } = useSession();
	if (member.access.finance === "none") {
		return (
			<Card>
				<Empty>Only members with access to Finance see what is owed.</Empty>
			</Card>
		);
	}
	return <Listed side={side} search={search} onSearch={onSearch} />;
}

function Listed({ side, search, onSearch }: PaymentDocumentsProps) {
	const { status, page, document: opened } = search;
	const shown = useQuery(paymentsPageQueryOptions(side, { status, page }));
	// A page link the API no longer takes, from an old bookmark or edited by hand: the first page of the same status.
	const refused = page !== undefined && shown.error instanceof ApiError && shown.error.status === 400;
	useEffect(() => {
		if (refused) {
			onSearch({ status });
		}
	}, [refused, status, onSearch]);
	const to = SIDE_ROUTES[side];

	/** Shows one status, or every status. A page link names the list it was made for, so the page goes. */
	function showStatus(option: PaymentStatus | undefined) {
		onSearch({ status: option });
	}

	const columns: readonly TableColumn<PaymentDocument>[] = [
		{
			key: "due",
			header: "Due",
			cell: document =>
				document.dueOn === undefined ? (
					<span className="text-ink-3">—</span>
				) : (
					<time
						dateTime={document.dueOn}
						className={cn("whitespace-nowrap", document.status === "overdue" && "text-alert")}
					>
						{formatShortDate(document.dueOn)}
					</time>
				),
		},
		{
			key: "document",
			header: "Document",
			cell: document => {
				const also = [document.counterparty, document.number].filter(
					(part): part is string => part !== undefined,
				);
				return (
					<span className="block min-w-0">
						<Link
							to={to}
							search={{ ...search, document: document.fileId }}
							className="block font-medium break-all text-ink hover:underline"
						>
							{document.fileName}
						</Link>
						{also.length === 0 ? null : (
							<span className="block text-caption text-ink-3">{also.join(" · ")}</span>
						)}
					</span>
				);
			},
		},
		...(side === "payables"
			? [
					{
						key: "kind",
						header: "Kind",
						muted: true,
						cell: (document: PaymentDocument) => PAYMENT_DOCUMENT_KIND_LABELS[document.kind],
					},
				]
			: []),
		{
			key: "amount",
			header: "Amount",
			align: "right",
			cell: ({ total }) => (
				<span className="whitespace-nowrap">{formatAmount(total.amount, total.currency)}</span>
			),
		},
		{
			key: "status",
			header: "Status",
			cell: document => (
				<Pill tone={PAYMENT_STATUS_TONES[document.status]}>{PAYMENT_STATUS_LABELS[document.status]}</Pill>
			),
		},
	];

	const counts = shown.data?.counts;
	const everyStatus =
		counts === undefined ? undefined : PAYMENT_STATUSES.reduce((sum, each) => sum + counts[each], 0);
	return (
		<>
			<Toolbar>
				<FilterPill
					active={status === undefined}
					count={everyStatus}
					onClick={() => {
						showStatus(undefined);
					}}
				>
					All
				</FilterPill>
				{PAYMENT_STATUSES.map(option => (
					<FilterPill
						key={option}
						active={status === option}
						count={counts?.[option]}
						onClick={() => {
							showStatus(status === option ? undefined : option);
						}}
					>
						{PAYMENT_STATUS_LABELS[option]}
					</FilterPill>
				))}
			</Toolbar>
			<Card>
				<CardHeader
					title={
						shown.data === undefined
							? "Documents"
							: `${plural(shown.data.total, "document", "documents")} in ${String(shown.data.year)}`
					}
				/>
				{refused || shown.isPending ? (
					<div role="status" aria-label="Loading the documents" className="flex flex-col gap-3 p-4">
						<Skeleton className="h-3.5 w-2/3" />
						<Skeleton className="h-3.5 w-1/2" />
						<Skeleton className="h-3.5 w-3/5" />
					</div>
				) : shown.isError ? (
					<p role="alert" className="px-4 py-3.5 text-caption text-alert">
						{shown.error.message}
					</p>
				) : shown.data.documents.length > 0 ? (
					<Table
						label={side === "payables" ? "Payables" : "Receivables"}
						columns={columns}
						rows={shown.data.documents}
						rowKey={document => document.fileId}
						interactiveRows
					/>
				) : (
					<Empty>
						{shown.data.total > 0 ? "Nothing is left on this page." : EMPTY_TEXT[side][status ?? "all"]}
					</Empty>
				)}
				{shown.data === undefined || (shown.data.next === null && shown.data.previous === null) ? null : (
					<Pager
						label="Pages"
						previous={
							shown.data.previous === null ? null : (
								<Link
									to={to}
									search={{ status, page: shown.data.previous }}
									className={buttonClassName({ size: "sm" })}
								>
									Previous
								</Link>
							)
						}
						next={
							shown.data.next === null ? null : (
								<Link
									to={to}
									search={{ status, page: shown.data.next }}
									className={buttonClassName({ size: "sm" })}
								>
									Next
								</Link>
							)
						}
					/>
				)}
			</Card>
			<PaymentDocumentDrawer
				fileId={opened}
				onClose={() => {
					onSearch({ ...search, document: undefined });
				}}
			/>
		</>
	);
}
